import { Map } from "immutable";
import _forEach from "lodash/forEach";
import _cloneDeep from "lodash/cloneDeep";
import { formatDistanceToNow } from "date-fns";
import { parseTimestamp } from "utils/helpers/timestampHelper";

/**
 * Calculates if a kiosk is online based on its lastSeenTS timestamp.
 * A kiosk is considered online if lastSeenTS is within the last 15 seconds.
 * @param {string|Date} lastSeenTS - The last seen timestamp (UTC string or Date object)
 * @param {number} thresholdMs - The threshold in milliseconds (default: 15000)
 * @returns {boolean} True if the kiosk is online, false otherwise
 */
export const calculateIsOnline = (lastSeenTS, thresholdMs = 15000) => {
  if (!lastSeenTS) return false;

  const lastSeenDate = parseTimestamp(lastSeenTS);
  if (!lastSeenDate) return false;

  const now = new Date();
  const diff = now.getTime() - lastSeenDate.getTime();
  const isOnline = diff < thresholdMs;
  return isOnline;
};

export const kiosksBrowserMapStateToProps = (state, ownProps) => {
  const sidebarMode = `${state.getIn(["base", "mainSidebar", "mode"])}Extra`;

  const kiosksDataState = state.getIn(["data", "kiosks"], Map({}));
  const byId = kiosksDataState.toJS();
  const kiosks = {};
  const getSortableTitle = doc => (doc?.title || "Untitled Kiosk").toLowerCase();

  if (Object.keys(byId).length > 0) {
    const sortedKiosks = Object.keys(byId)
      .filter(key => key !== "revertCopy")
      .map(key => byId[key])
      .sort((a, b) => getSortableTitle(a).localeCompare(getSortableTitle(b)));

    _forEach(sortedKiosks, (doc, counter) => {
      const isOnline = calculateIsOnline(doc.lastSeenTS);
      doc.isOnline = isOnline;
      kiosks[counter] = {
        id: doc._id,
        columns: {
          1: {
            id: 1,
            label: doc.title || "Untitled Kiosk"
          },
          2: (() => {
            let lastSeenDateStr = "Online";
            if (!doc.isOnline) {
              const lastSeenDate = doc.lastSeenTS
                ? parseTimestamp(doc.lastSeenTS)
                : null;
              if (lastSeenDate) {
                lastSeenDateStr =
                  "Offline (for " + formatDistanceToNow(lastSeenDate) + ")";
              } else {
                lastSeenDateStr = "Offline";
              }
            }
            return {
              id: 2,
              label: doc.isOnline
                ? `<div id="indicator_online" class="status-indicator online"><div class="icon"></div><div class="label">${lastSeenDateStr}</div></div>`
                : `<div id="indicator_offline" class="status-indicator offline"><div class="icon"></div><div class="label">${lastSeenDateStr}</div></div>`
            };
          })(),
          3: (() => {
            const lastCompletedPublishDate = doc.lastCompletedPublishTS
              ? parseTimestamp(doc.lastCompletedPublishTS)
              : null;
            return {
              id: 3,
              label: lastCompletedPublishDate
                ? formatDistanceToNow(lastCompletedPublishDate, {
                    addSuffix: true
                  })
                : "Never",
              sortValue: lastCompletedPublishDate
                ? lastCompletedPublishDate.getTime()
                : 0
            };
          })()
        }
      };
    });
  }

  return {
    slugBase: "/kiosks",
    kiosks,
    sidebarMode,
    sidebarProps: {
      title: "",
      goBackAction: ""
    }
  };
};

/**
 * Maps Redux state and route params for the kiosk manager screen.
 * @param {object} state
 * @param {object} ownProps
 */
export const kioskManagerMapStateToProps = (state, ownProps) => {
  const { params } = ownProps.match;
  const { kioskID, section } = params;

  const userPreferencesSelector = ["kioskManager", "editor", kioskID];
  const userPreferences =
    state
      .getIn(["ui", "userPreferences", ...userPreferencesSelector], null)
      ?.toJS() || {};

  const user = state.getIn(["data", "user"], Map);
  const kioskTypesMap = state.getIn(["base", "kioskTypes"], Map({})).toJS();
  const allProjects = state.getIn(["data", "projects"], Map([])).toJS();
  const kiosksById = state.getIn(["data", "kiosks"], Map({})).toJS();
  let revertCopy = state.getIn(["data", "kiosks", "revertCopy"]);
  if (typeof revertCopy !== "undefined") {
    revertCopy = Map(revertCopy).toJS();
  }
  const managerState = state.getIn(["ui", "kioskManager"], Map({})).toJS();
  const { isLoading } = managerState;

  const kioskData = kiosksById[kioskID] || null;

  if (kioskData) {
    kioskData.isOnline = calculateIsOnline(kioskData.lastSeenTS);
  }

  const typeParam = "standard";
  const typeLayout = typeParam ? kioskTypesMap[typeParam] : null;

  let navigation = typeLayout ? typeLayout.navigation : null;
  let goBackAction = "/kiosks";
  let sideBarSlugBase = `${goBackAction}/${kioskID}`;
  let sidebarData = _cloneDeep(kioskData);
  let sideBarTitle = kioskData ? kioskData.title : "";
  let structure = null;
  let isFormDataDirty = false;

  if (section) {
    sideBarTitle =
      section !== "mainmenu" ? navigation[section].label : "Main Menu";
    sideBarSlugBase += `/${section}`;
    goBackAction += `/${kioskID}`;

    if (section === "systemReport") {
      navigation = managerState.forms.systemReport;
      sidebarData = { ...kioskData };
    } else if (section === "settings") {
      navigation = _cloneDeep(managerState.forms.settings);
      sidebarData = { ...kioskData };

      if (navigation && navigation.kioskAppVersion) {
        const kioskAppVersions = state
          .getIn(["data", "kioskAppVersions"], Map({}))
          .toJS();
        const osPlatform = kioskData ? kioskData.osPlatform : null;
        const os = osPlatform === "darwin" ? "mac" : "windows";
        const versionsForOS = kioskAppVersions[os] || [];

        const options = versionsForOS.map(function (entry) {
          return { label: entry.version, value: entry.version };
        });

        navigation.kioskAppVersion = {
          ...navigation.kioskAppVersion,
          type: "select",
          options
        };
      }
    }
  }
  const sidebarMode = `${state.getIn(["base", "mainSidebar", "mode"])}Extra`;

  return {
    username: user.get("email", null),
    isLoading,
    sidebarMode,
    kioskID,
    kioskType: typeParam,
    section,
    userPreferencesSelector,
    userPreferences,
    sidebarProps: {
      title: sideBarTitle,
      navigation,
      structure,
      sidebarData,
      isFormDataDirty,
      section,
      slugBase: sideBarSlugBase,
      goBackAction
    },
    kioskData,
    kioskRevertCopy: revertCopy,
    allProjects,
    settings: state.get("base", Map).toJS()
  };
};
